import React, { useRef, useState, useEffect } from 'react'

import { Responsive as ResponsiveGridLayout } from 'react-grid-layout'

import type { InsightCardWidth, CustomGridBreakpointKey } from '@/types/dashboard-types'

import BreakpointToolbar from './BreakpointToolBar'
import GridOverlay from './GridOverlay'
import { useBreakpointManager } from './hooks/useBreakpointManager'
import { useGridLayout } from './hooks/useGridLayout'

import { Modal } from '../modal'

export interface LayoutItem {
  i: string
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
}

export interface CustomGridLayoutProps {
  defaultBreakpoint: CustomGridBreakpointKey
  isEditMode?: boolean
  cards: Array<{
    id: string
    element: React.ReactElement
    config: {
      width: InsightCardWidth
      height?: Partial<Record<CustomGridBreakpointKey, number>>
      aspectRatio: number
      position?: {
        lg?: { x: number; y: number; w: number; h: number }
        md?: { x: number; y: number; w: number; h: number }
        sm?: { x: number; y: number; w: number; h: number }
      }
    }
  }>
  onLayoutChange?: (
    cards: Array<{
      id: string
      element: React.ReactElement
      config: {
        width: InsightCardWidth
        height?: Partial<Record<CustomGridBreakpointKey, number>>
        aspectRatio: number
        position?: {
          lg?: { x: number; y: number; w: number; h: number }
          md?: { x: number; y: number; w: number; h: number }
          sm?: { x: number; y: number; w: number; h: number }
        }
      }
    }>
  ) => void
  onUnsavedChangesChange?: (hasUnsaved: boolean) => void
}

const BREAKPOINTS = {
  sm: 0,
  md: 768,
  lg: 1024,
}
export const COLS = {
  sm: 12,
  md: 12,
  lg: 24,
}

const PREVIEW_WIDTHS = {
  sm: 375,
  md: 768,
  lg: '100%',
}

const ROW_HEIGHT = 50
const GRID_MARGIN = 10 // react-grid-layout default margin

export default function CustomGridLayout({
  cards,
  onLayoutChange,
  onUnsavedChangesChange,
  defaultBreakpoint = 'lg',
  isEditMode = false,
}: CustomGridLayoutProps) {
  const breakpointManager = useBreakpointManager(isEditMode, defaultBreakpoint, onUnsavedChangesChange)
  const [currentBreakpoint, setCurrentBreakpoint] = useState<CustomGridBreakpointKey>('lg')

  const gridLayout = useGridLayout(
    cards,
    isEditMode,
    ROW_HEIGHT,
    onLayoutChange,
    onUnsavedChangesChange,
    breakpointManager.editingBreakpoint,
    currentBreakpoint,
    breakpointManager.setUnsavedBreakpoint
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth)

      const handleResize = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth)
        }
      }
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [])

  const getPreviewWidth = (): number => {
    if (!breakpointManager.editingBreakpoint) return containerWidth || 1024

    if (breakpointManager.editingBreakpoint === 'lg') {
      return containerWidth || 1024
    }

    const width = PREVIEW_WIDTHS[breakpointManager.editingBreakpoint]
    return typeof width === 'number' ? width : containerWidth || 1024
  }

  const handleDiscardChanges = () => {
    const targetBreakpoint = breakpointManager.pendingBreakpointSwitch

    breakpointManager.setUnsavedBreakpoint(null)
    onUnsavedChangesChange?.(false)
    breakpointManager.setUserHasInteracted(false)

    gridLayout.setMergedCards(cards)

    if (targetBreakpoint) {
      breakpointManager.setEditingBreakpoint(targetBreakpoint)
    }

    breakpointManager.handleCancelSwitch()
  }

  const getBreakpointStyles = () => {
    if (!breakpointManager.editingBreakpoint) return

    const borderColor = '#3399f3'

    const baseStyles = {
      border: `3px solid ${borderColor}`,
      boxShadow: `0 0 0 4px ${borderColor}20`,
      borderRadius: '4px',
      transition: 'all 0.3s ease',
    }

    const breakpointToUse = breakpointManager.editingBreakpoint || currentBreakpoint

    if (breakpointToUse === 'lg') {
      return baseStyles
    }

    const previewWidths: Record<CustomGridBreakpointKey, string> = {
      lg: '100%',
      md: '768px',
      sm: '375px',
    }

    return {
      ...baseStyles,
      width: previewWidths[breakpointToUse],
      maxWidth: previewWidths[breakpointToUse],
      margin: '0 auto',
    }
  }

  return (
    <div className="flex flex-col justify-center items-center w-full gap-2">
      {isEditMode && (
        <BreakpointToolbar
          activeBreakpoint={breakpointManager.editingBreakpoint || currentBreakpoint}
          onBreakpointChange={breakpointManager.handleBreakpointChangeRequest}
          currentViewport={defaultBreakpoint}
        />
      )}
      <div
        ref={containerRef}
        data-testid="custome-grid-layout"
        className="w-full relative"
        style={getBreakpointStyles()}
      >
        <GridOverlay
          isEditMode={isEditMode}
          cols={COLS[breakpointManager.editingBreakpoint || currentBreakpoint]}
          width={getPreviewWidth()}
          rowHeight={ROW_HEIGHT}
          gridMargin={GRID_MARGIN}
        />
        <ResponsiveGridLayout
          width={getPreviewWidth()}
          className="layout"
          layouts={gridLayout.layouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          margin={[GRID_MARGIN, GRID_MARGIN]}
          containerPadding={[GRID_MARGIN, GRID_MARGIN]}
          onBreakpointChange={(newBreakpoint) => {
            setCurrentBreakpoint(newBreakpoint as CustomGridBreakpointKey)
          }}
          onDragStart={() => breakpointManager.setUserHasInteracted(true)}
          onResizeStart={() => breakpointManager.setUserHasInteracted(true)}
          onLayoutChange={(currentLayout) => {
            gridLayout.handleLayoutChange(currentLayout, breakpointManager.userHasInteracted)
          }}
          isDraggable={isEditMode ? true : false}
          draggableHandle=".drag-handle"
          isResizable={isEditMode ? true : false}
          compactType="vertical"
          preventCollision={false}
          resizeHandles={['se']}
        >
          {gridLayout.mergedCards.map((card) => {
            return <div key={card.id}>{card.element}</div>
          })}
        </ResponsiveGridLayout>
      </div>

      <Modal
        show={breakpointManager.showUnsavedModal}
        onHide={breakpointManager.handleCancelSwitch}
        variation="warning"
        title="Unsaved Changes"
        bodyContent={
          <>
            <p>
              You have unsaved changes for the <strong>{breakpointManager.unsavedBreakpoint?.toUpperCase()}</strong>{' '}
              layout. If you switch breakpoints now, these changes will be lost.
            </p>
            <p className="mt-2">
              Click "Save" in the top menu to keep your changes, or "Discard Changes" to switch without saving.
            </p>
          </>
        }
        footerButtons={[
          { label: 'Discard Changes', onClick: handleDiscardChanges },
          { label: 'Cancel', onClick: breakpointManager.handleCancelSwitch },
        ]}
      />
    </div>
  )
}
